import { type WorkStatus } from "@pericles/shared";

export type WorkplanStatus = WorkStatus;

export interface ProjectChecklistItem {
  id: string;
  label: string;
  category?: string;
  status: WorkplanStatus;
  owner?: string;
  notes?: string;
}

export interface WorkPackage {
  id: string;
  title: string;
  summary: string;
  category?: string;
  status: WorkplanStatus;
  relatedRequirements?: string[];
  relatedSpecSections?: string[];
  checklist?: ProjectChecklistItem[];
}

export interface ProjectWorkplan {
  projectId?: string;
  overview?: string;
  workPackages: WorkPackage[];
  checklist?: ProjectChecklistItem[];
  assumptions?: string[];
  risks?: string[];
}

interface GenerateProjectWorkplanResponse {
  workplan?: ProjectWorkplan;
  error?: string;
}

interface ProjectWorkplanResponse {
  ok?: boolean;
  workplan?: ProjectWorkplan;
  updatedAt?: string;
  error?: string;
}

interface GenerateWorkplanOptions {
  dryRun?: boolean;
}

export interface ProjectWorkplanResult {
  workplan: ProjectWorkplan;
  updatedAt?: string;
}

export async function generateProjectWorkplan(
  projectId: string,
  options: GenerateWorkplanOptions = {},
): Promise<ProjectWorkplanResult> {
  if (!projectId) {
    throw new Error("project id is required");
  }

  const bodyPayload: Record<string, unknown> = {};

  if (options.dryRun) {
    bodyPayload.dryRun = true;
  }

  const response = await fetch(`/api/agent/projects/${projectId}/workplan`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(bodyPayload),
  });

  const payload = (await response.json()) as GenerateProjectWorkplanResponse;

  if (!response.ok) {
    const message = payload?.error ?? `Failed to generate workplan (status ${response.status})`;
    throw new Error(message);
  }

  if (!payload?.workplan) {
    throw new Error("No workplan was returned by the server");
  }

  return { workplan: payload.workplan };
}

export async function fetchProjectWorkplan(projectId: string): Promise<ProjectWorkplanResult | null> {
  if (!projectId) {
    throw new Error("project id is required");
  }

  const response = await fetch(`/api/projects/${projectId}/workplan`);
  const payload = (await response.json()) as ProjectWorkplanResponse;

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    const message = payload?.error ?? `Failed to load workplan (status ${response.status})`;
    throw new Error(message);
  }

  if (!payload?.workplan) {
    throw new Error("No workplan was returned by the server");
  }

  return { workplan: payload.workplan, updatedAt: payload.updatedAt };
}

export async function updateProjectWorkplan(
  projectId: string,
  workplan: ProjectWorkplan,
): Promise<ProjectWorkplanResult> {
  if (!projectId) {
    throw new Error("project id is required");
  }

  if (!workplan || !Array.isArray(workplan.workPackages)) {
    throw new Error("workplan with workPackages is required");
  }

  const response = await fetch(`/api/projects/${projectId}/workplan`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ workplan }),
  });

  const payload = (await response.json()) as ProjectWorkplanResponse;

  if (!response.ok) {
    const message = payload?.error ?? `Failed to update workplan (status ${response.status})`;
    throw new Error(message);
  }

  if (!payload?.workplan) {
    throw new Error("No workplan was returned by the server");
  }

  return { workplan: payload.workplan, updatedAt: payload.updatedAt };
}
